<template>
  <div>
    <FormGroup>
      <FormField
        label="Monthly income"
        hint="2x the monthly rent in total household income is the typical standard nationwide"
        :error="form.fieldErrors('minimumIncome')"
      >
        <Select v-model="form.fields.minimumIncome">
          <option v-for="option in minimumIncomeOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <View direction="column" gap="sm">
        <FormField label="Minimum credit score" :error="form.fieldErrors('minimumCreditScore')">
          <Input v-model="form.fields.minimumCreditScore" type="number" />
        </FormField>

        <HintCollapsible
          class="hint"
          :initially-expanded="true"
          icon="question-mark-circle"
          label="Credit Score Ranges based on TransUnion"
        >
          <ul>
            <li v-for="option in minimumCreditScoreOptions" :key="option">
              <Text variant="body-small">{{ option }}</Text>
            </li>
          </ul>
        </HintCollapsible>
      </View>

      <FormField
        label="Accept co-signer"
        direction="row"
        label-position="after"
        :error="form.fieldErrors('acceptsCosigners')"
      >
        <Checkbox v-model="form.fields.acceptsCosigners" name="acceptsCosigners" />
      </FormField>

      <Divider />

      <View direction="column" gap="sm">
        <FormField label="Renter qualification" direction="column">
          <RadioButtonGroup direction="column">
            <RadioCard
              v-for="option in screeningSensitivityOptions"
              :key="option.value"
              v-model="form.fields.screeningSensitivity"
              name="screeningSensitivity"
              :value="option.value"
            >
              <View gap="sm" direction="column">
                <View gap="sm" direction="row" align="center">
                  <Icon :name="option.icon" size="medium" />
                  <Text variant="label-medium">{{ option.title }}</Text>
                </View>
                <Text variant="body-medium" color="secondary">
                  {{ option.description }}
                </Text>
              </View>
            </RadioCard>
          </RadioButtonGroup>
        </FormField>

        <Hint>
          This information is intended to help guide Tallo to foster completed conversations and won't be displayed to a
          Renter
        </Hint>
      </View>
    </FormGroup>
  </div>
</template>

<script setup lang="ts">
import {
  Checkbox,
  FormField,
  FormGroup,
  HintCollapsible,
  Input,
  Select,
  View,
  Text,
  useForm,
  Divider,
  RadioButtonGroup,
  RadioCard,
  Icon,
  IconName,
  Hint,
} from '@tallo/design-system';
import { useInvestorStore } from '@tallo/investor/investor';
import { RenterRequirementsMinimumIncome, minimumCreditScoreOptions, ScreeningSensitivity } from '@tallo/property';

import { SavePropertyRenterRequirementsDto } from '../../dtos/save-renter-requirements.dto.interface';
import { useDetailsHolder } from '../../composables/details-holder';

const minimumIncomeOptions = Object.values(RenterRequirementsMinimumIncome);

const screeningSensitivityOptions: Array<{
  value: ScreeningSensitivity;
  title: string;
  description: string;
  icon: IconName;
}> = [
  {
    value: ScreeningSensitivity.STRICT,
    title: 'Strict',
    description:
      'Only renters who meet or exceed all criteria get approved. Highest quality leads but fewer applicants overall.',
    icon: 'user-check-02',
  },
  {
    value: ScreeningSensitivity.MODERATE,
    title: 'Moderate',
    description:
      'Allows reasonable compromises on requirements while maintaining standards. Balanced quality and lead volume.',
    icon: 'users-01',
  },
  {
    value: ScreeningSensitivity.OWNER_REVIEW,
    title: 'Owner review',
    description: 'No automatic rejections. Most leads possible but requires time to manually review all applications.',
    icon: 'users-plus',
  },
];

const holder = useDetailsHolder();
const investorStore = useInvestorStore();

const form = useForm<SavePropertyRenterRequirementsDto>({
  minimumIncome: holder.details?.renterRequirements?.minimumIncome || RenterRequirementsMinimumIncome.X2_RENT,
  minimumCreditScore: holder.details?.renterRequirements?.minimumCreditScore || 600,
  acceptsCosigners: holder.details?.renterRequirements?.acceptsCosigners || null,
  screeningSensitivity:
    holder.details?.renterRequirements?.screeningSensitivity ||
    investorStore.investor?.preferredScreeningSensitivity ||
    ScreeningSensitivity.MODERATE,
});

async function save() {
  return form.submit(async (fields) => await holder.saveRenterRequirements(fields));
}

defineExpose({ save, form });
</script>

<style scoped lang="scss">
ul {
  list-style-type: disc;
  margin-left: var(--t-spacing-md);
}
</style>
